import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import usePageTitle from "@/hooks/usePageTitle";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger
} from "@/components/ui/collapsible";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  FileSpreadsheet, Download, Printer, RefreshCw, Calendar,
  ChevronDown, ChevronRight, Package, CheckCircle2, AlertCircle,
  Clock, FileDown, TrendingUp, ShieldCheck, ExternalLink, FileText
} from "lucide-react";
import jsPDF from "jspdf";
import "jspdf-autotable";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  verified: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", icon: ShieldCheck, label: "Verified", iconColor: "text-blue-600" },
  complete: { bg: "bg-green-50", border: "border-green-200", text: "text-green-700", icon: CheckCircle2, label: "Complete", iconColor: "text-green-600" },
  partial:  { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", icon: Clock, label: "Partial", iconColor: "text-amber-600" },
  pending:  { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", icon: AlertCircle, label: "Pending", iconColor: "text-red-600" },
};

const PendingShipmentCostingReportPage = () => {
  usePageTitle("Pending Shipment Costing Report");
  const { token, user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedYears, setExpandedYears] = useState(new Set());
  const [verifyDialog, setVerifyDialog] = useState(null); // {year_month, month_name}
  const [verifyNotes, setVerifyNotes] = useState("");
  const [verifying, setVerifying] = useState(false);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/reports/pending-shipment-costing?start_year=2018`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Failed to fetch");
      const data = await res.json();
      setReportData(data);
      const yearsToExpand = new Set();
      data.years?.forEach(y => {
        if (y.summary.pending > 0 || y.summary.partial > 0 || y.year === new Date().getFullYear()) {
          yearsToExpand.add(y.year);
        }
      });
      setExpandedYears(yearsToExpand);
    } catch (e) {
      toast.error("Failed to load report: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReport(); }, [token]);

  const toggleYear = (year) => {
    const s = new Set(expandedYears);
    s.has(year) ? s.delete(year) : s.add(year);
    setExpandedYears(s);
  };

  const handleMarkComplete = async () => {
    if (!verifyDialog) return;
    setVerifying(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/reports/pending-shipment-costing/mark-complete`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ year_month: verifyDialog.year_month, notes: verifyNotes })
      });
      if (!res.ok) throw new Error("Failed");
      toast.success(`${verifyDialog.month_name} marked as verified`);
      setVerifyDialog(null);
      setVerifyNotes("");
      fetchReport();
    } catch (e) {
      toast.error("Failed to verify: " + e.message);
    } finally {
      setVerifying(false);
    }
  };

  const handleUnmark = async (year_month) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/reports/pending-shipment-costing/unmark-complete?year_month=${year_month}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Failed");
      toast.success("Verification removed");
      fetchReport();
    } catch (e) {
      toast.error("Failed: " + e.message);
    }
  };

  const openMonthDetail = (year, month) => {
    window.open(`/reports/month-detail?year=${year}&month=${month}`, "_blank");
  };

  const formatCurrency = (val) => {
    if (!val) return "₹0";
    if (val >= 10000000) return `₹${(val / 10000000).toFixed(1)}Cr`;
    if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
    return `₹${val.toLocaleString("en-IN")}`;
  };

  const exportToPDF = () => {
    if (!reportData) return;
    const doc = new jsPDF();
    const pw = doc.internal.pageSize.getWidth();
    doc.setFontSize(16);
    doc.text("Pending Shipment Costing Report", pw / 2, 18, { align: "center" });
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(`Generated: ${new Date().toLocaleString()}`, pw / 2, 25, { align: "center" });

    let y = 35;
    reportData.years.slice().reverse().forEach(yd => {
      if (y > 250) { doc.addPage(); y = 20; }
      doc.setFontSize(12);
      doc.setTextColor(40);
      doc.text(`${yd.year}`, 14, y);
      doc.autoTable({
        startY: y + 4,
        head: [["Month", "Parcels", "Packing %", "Costing %", "Sheets", "Status"]],
        body: yd.months.map(m => [
          m.month_name, m.parcel_count, `${m.packing_coverage}%`, `${m.costing_completion}%`,
          m.sheets.join(", ") || "-", m.status.charAt(0).toUpperCase() + m.status.slice(1)
        ]),
        theme: "striped",
        headStyles: { fillColor: [51, 65, 85], fontSize: 8 },
        styles: { fontSize: 7 }
      });
      y = doc.lastAutoTable.finalY + 12;
    });
    doc.save("pending-shipment-costing.pdf");
    toast.success("PDF exported");
  };

  const exportToCSV = () => {
    if (!reportData) return;
    let csv = "Year,Month,Parcels,Packing%,Costing%,Errors,Sheets,Status\n";
    reportData.years.forEach(yd => yd.months.forEach(m => {
      csv += `${yd.year},${m.month_name},${m.parcel_count},${m.packing_coverage},${m.costing_completion},${m.with_errors},"${m.sheets.join("; ")}",${m.status}\n`;
    }));
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "pending-shipment-costing.csv";
    a.click();
    toast.success("CSV exported");
  };

  if (loading) return (
    <div className="p-6 space-y-4">
      <Skeleton className="h-10 w-80" />
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-20" />)}</div>
      <Skeleton className="h-64" />
    </div>
  );

  const s = reportData?.summary || {};

  return (
    <div className="p-4 md:p-6 space-y-5">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Package className="w-6 h-6 text-blue-600" />
            Upload Tracker
          </h1>
          <p className="text-sm text-slate-500">Monthly order sheet & packing list upload status</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchReport}><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm"><Download className="w-4 h-4 mr-1" />Export<ChevronDown className="w-3 h-3 ml-1" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={exportToPDF}><FileDown className="w-4 h-4 mr-2" />PDF</DropdownMenuItem>
              <DropdownMenuItem onClick={exportToCSV}><FileSpreadsheet className="w-4 h-4 mr-2" />CSV</DropdownMenuItem>
              <DropdownMenuItem onClick={() => window.print()}><Printer className="w-4 h-4 mr-2" />Print</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: "Total Months", val: s.total_months, color: "slate", icon: Calendar },
          { label: "Complete", val: s.completed_months, sub: `${s.completion_rate}%`, color: "green", icon: CheckCircle2 },
          { label: "Verified", val: s.verified_months, color: "blue", icon: ShieldCheck },
          { label: "Partial", val: s.partial_months, color: "amber", icon: Clock },
          { label: "Pending", val: s.pending_months, color: "red", icon: AlertCircle },
        ].map(({ label, val, sub, color, icon: Icon }) => (
          <Card key={label} className={`border-${color}-200 bg-${color}-50`}>
            <CardContent className="p-3 flex items-center justify-between">
              <div>
                <p className={`text-xs text-${color}-600`}>{label}</p>
                <p className={`text-xl font-bold text-${color}-700`}>{val || 0}</p>
                {sub && <p className={`text-xs text-${color}-500`}>{sub}</p>}
              </div>
              <Icon className={`w-6 h-6 text-${color}-400`} />
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Year-wise Breakdown */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><TrendingUp className="w-5 h-5 text-blue-600" />Year-wise Status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {reportData?.years?.slice().reverse().map((yd) => (
            <Collapsible key={yd.year} open={expandedYears.has(yd.year)} onOpenChange={() => toggleYear(yd.year)}>
              <CollapsibleTrigger asChild>
                <div className="flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 rounded-lg cursor-pointer border border-slate-200" data-testid={`year-row-${yd.year}`}>
                  <div className="flex items-center gap-3">
                    {expandedYears.has(yd.year) ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                    <span className="font-semibold text-slate-800">{yd.year}</span>
                    <div className="hidden md:flex items-center gap-2 ml-3">
                      <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div className="h-full bg-green-500 rounded-full" style={{ width: `${yd.summary.completion_rate}%` }} />
                      </div>
                      <span className="text-xs text-slate-500">{yd.summary.completion_rate}%</span>
                    </div>
                  </div>
                  <div className="flex gap-2 text-xs">
                    {yd.summary.completed > 0 && <Badge className="bg-green-100 text-green-700 border-green-300">{yd.summary.completed} done</Badge>}
                    {yd.summary.verified > 0 && <Badge className="bg-blue-100 text-blue-700 border-blue-300">{yd.summary.verified} verified</Badge>}
                    {yd.summary.partial > 0 && <Badge className="bg-amber-100 text-amber-700 border-amber-300">{yd.summary.partial} partial</Badge>}
                    {yd.summary.pending > 0 && <Badge className="bg-red-100 text-red-700 border-red-300">{yd.summary.pending} pending</Badge>}
                  </div>
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="border border-t-0 border-slate-200 rounded-b-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100 text-slate-600">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">Month</th>
                        <th className="px-3 py-2 text-left font-medium">Status</th>
                        <th className="px-3 py-2 text-right font-medium">Parcels</th>
                        <th className="px-3 py-2 text-right font-medium hidden md:table-cell">Packing</th>
                        <th className="px-3 py-2 text-right font-medium hidden md:table-cell">Costing</th>
                        <th className="px-3 py-2 text-left font-medium hidden lg:table-cell">Sheets</th>
                        <th className="px-3 py-2 text-right font-medium hidden lg:table-cell">Total Cost</th>
                        <th className="px-3 py-2 text-center font-medium hidden md:table-cell">Alerts</th>
                        <th className="px-3 py-2 text-center font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {yd.months.map((m) => {
                        const cfg = statusConfig[m.status] || statusConfig.pending;
                        const StatusIcon = cfg.icon;
                        return (
                          <tr key={m.year_month} className={`${cfg.bg} border-t ${cfg.border}`} data-testid={`month-row-${m.year_month}`}>
                            <td className="px-3 py-2 font-medium text-slate-800">{m.month_name}</td>
                            <td className="px-3 py-2">
                              <span className={`inline-flex items-center gap-1 text-xs font-medium ${cfg.text}`}>
                                <StatusIcon className={`w-3.5 h-3.5 ${cfg.iconColor}`} />
                                {cfg.label}
                              </span>
                              {m.verification && (
                                <span className="block text-[10px] text-blue-500 mt-0.5">by {m.verification.verified_by}</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right font-mono">
                              {m.parcel_count > 0 ? m.parcel_count.toLocaleString() : "-"}
                              {m.below_threshold && <span className="block text-[10px] text-amber-500 font-normal">below {m.parcel_threshold}</span>}
                            </td>
                            <td className="px-3 py-2 text-right hidden md:table-cell">
                              {m.parcel_count > 0 ? (
                                <div className="flex items-center justify-end gap-2">
                                  <div className="w-12 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                    <div className={`h-full rounded-full ${m.packing_coverage === 100 ? 'bg-green-500' : m.packing_coverage > 0 ? 'bg-amber-500' : 'bg-red-400'}`} style={{ width: `${m.packing_coverage}%` }} />
                                  </div>
                                  <span className="text-xs font-mono">{m.packing_coverage}%</span>
                                </div>
                              ) : "-"}
                            </td>
                            <td className="px-3 py-2 text-right hidden md:table-cell">
                              {m.parcel_count > 0 ? (
                                <div className="flex items-center justify-end gap-2">
                                  <div className="w-12 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                    <div className={`h-full rounded-full ${m.costing_completion === 100 ? 'bg-green-500' : 'bg-amber-500'}`} style={{ width: `${m.costing_completion}%` }} />
                                  </div>
                                  <span className="text-xs font-mono">{m.costing_completion}%</span>
                                </div>
                              ) : "-"}
                            </td>
                            <td className="px-3 py-2 hidden lg:table-cell">
                              <div className="flex flex-wrap gap-1">
                                {m.sheets.slice(0, 4).map(sh => (
                                  <span key={sh} className="text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">{sh}</span>
                                ))}
                                {m.sheets.length > 4 && <span className="text-[10px] text-slate-400">+{m.sheets.length - 4}</span>}
                              </div>
                            </td>
                            <td className="px-3 py-2 text-right font-mono text-xs hidden lg:table-cell">{m.parcel_count > 0 ? formatCurrency(m.total_cost) : "-"}</td>
                            <td className="px-3 py-2 text-center hidden md:table-cell">
                              {(m.price_alerts?.high > 0 || m.price_alerts?.low > 0) ? (
                                <div className="flex items-center justify-center gap-1">
                                  {m.price_alerts?.high > 0 && <span className="text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-medium">{m.price_alerts.high} high</span>}
                                  {m.price_alerts?.low > 0 && <span className="text-[10px] bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded font-medium">{m.price_alerts.low} low</span>}
                                </div>
                              ) : m.parcel_count > 0 ? <span className="text-[10px] text-green-600">OK</span> : "-"}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <div className="flex items-center justify-center gap-1">
                                {m.parcel_count > 0 && (
                                  <Button
                                    variant="ghost" size="sm"
                                    className="h-7 px-2 text-xs text-blue-600 hover:text-blue-800"
                                    data-testid={`view-detail-${m.year_month}`}
                                    onClick={() => openMonthDetail(yd.year, m.month)}
                                  >
                                    <ExternalLink className="w-3 h-3 mr-1" />View
                                  </Button>
                                )}
                                {isAdmin && (m.status === "partial" || m.status === "pending") && (
                                  <Button
                                    variant="ghost" size="sm"
                                    className="h-7 px-2 text-xs text-emerald-600 hover:text-emerald-800"
                                    data-testid={`mark-complete-${m.year_month}`}
                                    onClick={() => { setVerifyDialog({ year_month: m.year_month, month_name: `${m.month_name} ${yd.year}` }); setVerifyNotes(""); }}
                                  >
                                    <ShieldCheck className="w-3 h-3 mr-1" />Verify
                                  </Button>
                                )}
                                {isAdmin && m.status === "verified" && (
                                  <Button
                                    variant="ghost" size="sm"
                                    className="h-7 px-2 text-xs text-red-500 hover:text-red-700"
                                    data-testid={`unmark-${m.year_month}`}
                                    onClick={() => handleUnmark(m.year_month)}
                                  >
                                    Unmark
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CollapsibleContent>
            </Collapsible>
          ))}
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs text-slate-500">
        {Object.entries(statusConfig).map(([key, cfg]) => {
          const Icon = cfg.icon;
          return (
            <span key={key} className="flex items-center gap-1"><Icon className={`w-3 h-3 ${cfg.iconColor}`} />{cfg.label}</span>
          );
        })}
      </div>

      {/* Verify Dialog */}
      <Dialog open={!!verifyDialog} onOpenChange={() => setVerifyDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-blue-600" />Mark as Verified</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">Mark <strong>{verifyDialog?.month_name}</strong> as verified complete. This confirms all uploads for this month are accounted for.</p>
          <Input
            placeholder="Notes (optional) — e.g., No shipments this month"
            value={verifyNotes}
            onChange={(e) => setVerifyNotes(e.target.value)}
            data-testid="verify-notes-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setVerifyDialog(null)}>Cancel</Button>
            <Button onClick={handleMarkComplete} disabled={verifying} data-testid="confirm-verify-btn">
              {verifying ? "Verifying..." : "Confirm Verify"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PendingShipmentCostingReportPage;
